<template>
    <div class="account-details-memberships-tab tab-contents">
        <NoResults v-if="totalItems < 1"
                   icon="fa-solid fa-users-slash"
                   color="light"
                   lang="MANAGE_ACCOUNT.NO_MEMBERSHIPS">
            <!-- New membership button -->
            <AddAccountToCartButton :account="account"
                                    :default-item-type-tab="membershipPlanItemType"
                                    button-class="mt-1 btn-info"
                                    data-analytics-id="Account Details / Memberships Tab / Add Account To Cart" />
        </NoResults>
        <template v-else>
            <MembershipsList :memberships="visibleMemberships"
                             :selected-memberships="selectedMemberships"
                             :edit-mode="inMembershipEditMode"
                             @select="toggleMembershipSelected"
                             @edit="editMembership"
                             @click="clickMembership">
                <span>
                    <!-- New membership button -->
                    <AddAccountToCartButton :account="account"
                                            :default-item-type-tab="membershipPlanItemType"
                                            button-class="btn-info"
                                            data-analytics-id="Account Details / Memberships Tab / Add Account To Cart" />

                    <!-- Edit button -->
                    <button v-if="inMembershipEditMode"
                            class="btn btn-default ml-05"
                            @click="toggleEditMode">
                        <i class="fa-solid fa-circle-x"></i>
                        {{ l('MANAGE_ACCOUNT_MEMBERSHIPS.CANCEL') }}
                    </button>
                    <button v-else
                            class="btn btn-default ml-05"
                            @click="toggleEditMode">
                        <i class="fa-solid fa-pencil"></i>
                        {{ l('MANAGE_ACCOUNT_MEMBERSHIPS.EDIT') }}
                    </button>

                    <!-- Unselect all button if edit mode is on and everything selectable is selected -->
                    <button v-if="inMembershipEditMode && hasSelectableMemberships && allActiveMembershipsSelected"
                            class="btn btn-default ml-05 memberships-list__unselect-all-btn"
                            @click="deselectAllMemberships">
                        <i class="fa-solid fa-toggle-off"></i>
                        {{ l('MANAGE_ACCOUNT.UNSELECT_ALL') }}
                    </button>
                    <!-- Select all button if edit mode is on and not everything selectable is selected -->
                    <button v-else-if="inMembershipEditMode && hasSelectableMemberships"
                            class="btn btn-default ml-05 memberships-list__select-all-btn"
                            @click="selectAllMemberships">
                        <i class="fa-solid fa-toggle-on"></i>
                        {{ l('MANAGE_ACCOUNT_MEMBERSHIPS.SELECT_ALL') }}
                    </button>
                </span>
            </MembershipsList>

            <Pager v-model="currentPage"
                   class="mt-1"
                   :total-items="totalItems"
                   :page-size="pageSize" />
        </template>

        <!-- Bottom bar appears when there are selected memberships -->
        <div :class="{'visible' : selectedMemberships.length >= 1}"
             class="footer-btn-bar account-memberships-footer-btn-bar">
            <span class="indicator indicator-success">
                {{ l('MANAGE_ACCOUNT.SELECTED', { count: selectedMemberships.length }) }}
            </span>

            <SquareBtn :disabled="stopRenewingInProgress || selectedMembershipsWithAutoRenewOn.length < 1"
                       :loading="stopRenewingInProgress"
                       @click="stopRenewingMemberships">
                <i class="fa-solid fa-stop-circle"></i>
                {{ l('MANAGE_ACCOUNT.STOP_RENEWING_MEMBERSHIPS_BTN') }}
            </SquareBtn>

            <button class="btn btn-default"
                    @click="cancelSelectedMemberships()">
                <i class="fa-solid fa-circle-x"></i>
                {{ l('MANAGE_ACCOUNT.CANCEL_MEMBERSHIPS_BTN') }}
            </button>

            <button class="btn btn-default"
                    @click="changeSelectedMembershipPlans()">
                <i class="fa-solid fa-right-left-large"></i>
                {{ l('MANAGE_ACCOUNT.CHANGE_MEMBERSHIP_PLANS_BTN') }}
            </button>
        </div>
    </div>
</template>

<script>
import AddAccountToCartButton from '@/epos/vue/pages/account-details/components/AddAccountToCartButton';
import ItemType from '@/lib/Items/ItemType';
import MembershipsList from '@/epos/vue/components/Memberships/MembershipsList';
import NoResults from '@/vue/components/NoResults';
import Pager from '@/vue/components/Pager';
import SquareBtn from '@/vue/components/SquareBtn';
import { ANGULAR_MODALS } from '@/lib/Modals/ANGULAR_MODALS';
import { container } from '@/container';
import { mapState } from 'vuex';

export default {
    components: { AddAccountToCartButton, NoResults, SquareBtn, MembershipsList, Pager },

    props: {
        /**
         * @type {CustomerAccount}
         */
        account: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            currentPage: 1,
            pageSize: 10,

            memberships: this.account.memberships,

            stopRenewingInProgress: false,
        };
    },

    computed: {
        ...mapState('accountDetails', ['selectedMemberships', 'inMembershipEditMode']),

        /**
         * @returns {Membership[]}
         */
        visibleMemberships() {
            // Return a chunk of this.memberships for the current page:
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            return this.memberships.slice(start, end);
        },

        /**
         * @returns {number}
         */
        totalItems() {
            return this.memberships.length;
        },

        /**
         * @returns {number}
         */
        totalActiveMemberships() {
            return this.memberships.filter((membership) => membership.isActive()).length;
        },

        /**
         * @returns {boolean}
         */
        hasSelectableMemberships() {
            return this.totalActiveMemberships > 0;
        },

        /**
         * @returns {boolean}
         */
        allActiveMembershipsSelected() {
            return this.selectedMemberships.length === this.totalActiveMemberships;
        },

        /**
         * @returns {Membership[]}
         */
        selectedMembershipsWithAutoRenewOn() {
            return this.selectedMemberships.filter((membership) => membership.willAutoRenew());
        },

        /**
         * @returns {string}
         */
        membershipPlanItemType() {
            return ItemType.MEMBERSHIP_PLAN;
        },
    },

    methods: {
        /**
         * @param {Membership} membership
         */
        toggleMembershipSelected(membership) {
            if (this.selectedMemberships.includes(membership)) {
                // Deselect the membership.
                this.$store.commit('accountDetails/deselectMembership', membership);
                return;
            }

            // Select the membership, but only if it is active.
            if (membership.isActive()) {
                this.$store.commit('accountDetails/selectMembership', membership);
            } else {
                // Setting the highlight status will cause the memberships to jiggle a bit to show its state.
                membership.setHighlightStatus(true);
                setTimeout(() => {
                    membership.setHighlightStatus(false);
                }, 500);
            }
        },

        selectAllMemberships() {
            this.deselectAllMemberships();
            this.memberships.forEach((membership) => {
                if (membership.isActive()) {
                    this.$store.commit('accountDetails/selectMembership', membership);
                }
            });
        },

        deselectAllMemberships() {
            this.$store.commit('accountDetails/clearSelectedMemberships');
        },

        toggleEditMode() {
            this.$store.commit('accountDetails/setMembershipEditMode', !this.inMembershipEditMode);
        },

        clickMembership(membership) {
            if (this.inMembershipEditMode) {
                this.toggleMembershipSelected(membership);
            } else {
                container.navigationService.viewMembershipDetails(membership.ref);
            }
        },

        editMembership(membership) {
            let self = this;

            let membershipDataset = membership.toMembershipDataset();

            // Show the MembershipDatasetsModal with only this dataset, and with only
            // the members tab enabled.

            // This is the user adding/editing the item, so show the pop-up.
            const modalInstance = container.modalFactory.openFromData(
                ANGULAR_MODALS.MEMBERSHIP_DATASETS,
                {
                    action: () => 'edit-existing',
                    membershipPlan: () => membership.currentPlan,
                    currentMemberID: () => null,
                    customerAccount: () => self.account,
                    increaseQuantityBy: () => 0,
                    membershipDatasets: () => [membershipDataset],
                },
            );

            modalInstance.then(
                (data) => {
                    let dataset = data.membershipDatasets[0];

                    container.membershipService.updateMembership(
                        membership,
                        dataset,
                        function (newMembership) {
                            this.replaceMembership(membership, newMembership);
                        },
                    );
                },
                () => {
                },
            );
        },

        cancelSelectedMemberships() {
            container.membershipManagementService.cancelMemberships(
                this.selectedMemberships,
                this.account.getAccountDetailsPath(),
                this.account
            );
        },

        stopRenewingMemberships() {
            if (this.stopRenewingInProgress) {
                return;
            }

            this.stopRenewingInProgress = true;
            container.membershipManagementService.stopRenewingMemberships(
                this.selectedMemberships,
                (result) => {
                    this.stopRenewingInProgress = false;
                    if (result) {
                        this.deselectAllMemberships();
                    }
                }
            );
        },

        changeSelectedMembershipPlans() {
            container.membershipManagementService.changeMembershipPlans(
                this.selectedMemberships,
                this.account.getAccountDetailsPath(),
                this.account
            );
        },

        replaceMembership(oldMembership, newMembership) {
            let index = this.memberships.indexOf(oldMembership);
            if (index !== -1) {
                this.memberships[index] = newMembership;
            }
        },
    },
};
</script>

<style lang="less">
.account-memberships-footer-btn-bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}
</style>
